import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router'
import { AlertCircle, Check, FileText } from 'lucide-react'
import { UPLOAD_ACCEPTED_LABEL, UPLOAD_MANUAL_ENTRY_NOTE, UPLOAD_ACCEPT_LIST } from '@klear/upload/upload-formats'
import { DOCUMENT_UPLOAD_REQUEST_TOKEN_PARAM } from '@klear/document-upload-request/wire.client'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import LoadingSpinner from '@klear/ui/components/spinner'
import { TraderCombobox } from '@klear/ui/components/trader-combobox'
import { useToast } from '@klear/ui/hooks/use-toast'
import { FileDropZone } from '@klear/ui/components/file-drop-zone'
import {
  useDocumentUploadRequest,
  useDocumentUploadRequestMutations,
  useDocumentUpload,
  type UploadFileStatus
} from '@klear/ui/hooks/use-document-upload-request'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export type DocumentUploadPageProps = {
  fixedTraderId?: number
}

export function FileRow({ row }: { row: UploadFileStatus }) {
  return (
    <li className="flex items-center gap-3 border-b py-2 text-sm last:border-b-0">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">{row.file.name}</span>
      {row.status === 'uploading' && <span className="text-muted-foreground">Uploading…</span>}
      {row.status === 'done' && <Check className="h-4 w-4 text-muted-foreground" />}
      {row.status === 'error' && <span className="text-destructive">{row.error ?? 'Failed'}</span>}
    </li>
  )
}

export function DocumentUploadPage({ fixedTraderId }: DocumentUploadPageProps) {
  const [searchParams] = useSearchParams()
  const token = searchParams.get(DOCUMENT_UPLOAD_REQUEST_TOKEN_PARAM)
  const { toast } = useToast()
  useDocumentTitle('Upload Document')

  const requestQuery = useDocumentUploadRequest(token)
  const { closeRequest, setRequestTrader, isBusy } = useDocumentUploadRequestMutations()
  const { upload, statuses, isUploading, reset } = useDocumentUpload()

  const [pickedTraderId, setPickedTraderId] = useState<number | null>(fixedTraderId ?? null)
  const [pending, setPending] = useState<File[]>([])
  const [finished, setFinished] = useState(false)
  const [uploadedCount, setUploadedCount] = useState(0)

  const request = requestQuery.data?.request
  const documents = requestQuery.data?.documents ?? []
  const requestExpired = requestQuery.data?.status === 'expired'

  const traderId = fixedTraderId ?? pickedTraderId ?? request?.traderId ?? null
  const traderLocked = fixedTraderId !== undefined

  const startUpload = useCallback(async () => {
    if (traderId == null) {
      toast({ title: 'Choose a trader first', variant: 'destructive' })
      return
    }

    if (token !== null && request !== undefined && request.traderId !== traderId) {
      try {
        await setRequestTrader({ token, traderId })
      } catch {
        return
      }
    }

    const outcome = await upload(pending, traderId, token)
    setPending([])
    if (outcome.uploadedCount === 0) {
      toast({
        title: 'Nothing was uploaded',
        description: 'Check the errors above and try again.',
        variant: 'destructive'
      })
      return
    }
    if (!outcome.recordedAgainstRequest) {
      toast({
        title: 'Uploaded, but not linked to this request',
        description: 'The link expired. The files are in Klear — search for them by name.',
        variant: 'destructive'
      })
    }
    setUploadedCount(outcome.uploadedCount)
    if (token !== null) {
      try {
        await closeRequest({ token })
      } catch {}
    }
    setFinished(true)
  }, [closeRequest, pending, request, setRequestTrader, toast, token, traderId, upload])

  if (token !== null && requestQuery.isLoading) {
    return (
      <div className="container mx-auto flex h-full items-center justify-center px-4 py-4">
        <LoadingSpinner />
      </div>
    )
  }

  if (token !== null && requestQuery.isError) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>This upload link is not valid</AlertTitle>
          <AlertDescription>
            It may have expired, or it may belong to a trader you cannot access. Ask for a new link.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (finished) {
    return (
      <div className="container mx-auto flex max-w-2xl flex-col gap-6 px-4 py-8">
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent">
            <Check className="h-6 w-6" />
          </span>
          <h1 className="text-2xl font-bold">
            {uploadedCount} {uploadedCount === 1 ? 'file uploaded' : 'files uploaded'}
          </h1>
          <p className="max-w-md text-base text-muted-foreground">
            {token === null
              ? `You’ll find ${uploadedCount === 1 ? 'it' : 'them'} in Documents.`
              : `Return to your chat and say you’ve uploaded. The agent will pick ${uploadedCount === 1 ? 'it' : 'them'} up from here.`}
          </p>
        </div>

        {documents.length > 0 && (
          <ul className="rounded-md border px-3">
            {documents.map((document) => (
              <li key={document.document.id} className="flex items-center gap-3 border-b py-2 text-sm last:border-b-0">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{document.document.name}</span>
              </li>
            ))}
          </ul>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={() => {
            reset()
            setUploadedCount(0)
            setFinished(false)
          }}
        >
          Upload another
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto flex max-w-2xl flex-col gap-6 px-4 py-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Upload a document</h1>
        <p className="text-sm text-muted-foreground">
          {token === null
            ? 'Add a document to a trader’s records.'
            : 'Add the document your chat asked for, then go back and say you’re done.'}
        </p>
      </header>

      {requestExpired && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>This upload link has expired</AlertTitle>
          <AlertDescription>Ask for a new link, then upload here.</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">Trader</span>
        {traderLocked ? (
          <p className="text-base">{request?.traderName ?? `Trader ${traderId}`}</p>
        ) : (
          <>
            <TraderCombobox value={traderId} onChange={setPickedTraderId} label={null} />
            {request?.traderId !== undefined && traderId !== request.traderId && (
              <p className="text-sm text-muted-foreground">
                Changed from {request.traderName ?? `trader ${request.traderId}`}. The upload will be filed here
                instead.
              </p>
            )}
          </>
        )}
      </div>

      {request?.purpose !== undefined && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">What was asked for</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-base">{request.purpose}</p>
          </CardContent>
        </Card>
      )}

      {!requestExpired && (
        <>
          <FileDropZone
            files={pending}
            onFilesChange={setPending}
            accept={UPLOAD_ACCEPT_LIST}
            acceptedLabel={UPLOAD_ACCEPTED_LABEL}
            acceptedNote={UPLOAD_MANUAL_ENTRY_NOTE}
            disabled={isUploading}
          />

          {statuses.length > 0 && (
            <ul className="rounded-md border px-3">
              {statuses.map((row, index) => (
                <FileRow key={`${row.file.name}-${index}`} row={row} />
              ))}
            </ul>
          )}

          <Button
            type="button"
            onClick={startUpload}
            disabled={pending.length === 0 || isUploading || isBusy || traderId == null}
          >
            {isUploading && 'Uploading…'}
            {!isUploading && pending.length === 0 && 'Upload files'}
            {!isUploading &&
              pending.length > 0 &&
              `Upload ${pending.length} ${pending.length === 1 ? 'file' : 'files'}`}
          </Button>
        </>
      )}
    </div>
  )
}
